import React, { useMemo } from "react";
import type { AssistantArtifactPart } from "../../../chat/types";
import { useChatServices } from "../providers/ChatProvider";
import { ArtifactCodeCard } from "./ArtifactCodeCard";
import { ArtifactHtmlCard } from "./ArtifactHtmlCard";
import { ArtifactTableCard } from "./ArtifactTableCard";
import { ArtifactFileCard } from "./ArtifactFileCard";
import { ArtifactDocCard } from "./ArtifactDocCard";
import { ArtifactApprovalBar } from "./ArtifactApprovalBar";
import { CollapsibleArtifact } from "./CollapsibleArtifact";
import { ArtifactExportMenu } from "./ArtifactExportMenu";
import { isMarkdownMime } from "./artifactFilename";
import { readArtifactBody } from "./artifactTable";
import { ArtifactTablePreview } from "./ArtifactTablePreview";
import {
  MARKDOWN_EXPORT_FORMATS,
  TABLE_EXPORT_FORMATS,
  exportFormatsFor,
} from "./artifactExportFormats";

export function ArtifactCard({ part }: { part: AssistantArtifactPart }) {
  // The stable slice: the full context changes on every streamed flush, and
  // a card per artifact re-rendering per token stalled big reviews
  // (ELASV1-3514).
  const { registry, artifactsApi } = useChatServices();
  const exportOnly = part.display === "export_only";
  // Parsed once per body, not per render: a 4,800-row table is ~5 ms. An
  // export-only table is not read at all (the server sends no body).
  const tableExportable = useMemo(
    () =>
      part.artifact_kind === "table" &&
      (exportOnly || readArtifactBody(part.text).status !== "unexportable"),
    [part.artifact_kind, part.text, exportOnly],
  );
  const custom = registry.getRenderer("artifact_card");
  if (custom) {
    return <>{custom(part as any)}</>;
  }
  const inner = (() => {
    switch (part.artifact_kind) {
      case "code":  return <ArtifactCodeCard part={part} />;
      case "html":  return <ArtifactHtmlCard part={part} />;
      case "table": return <ArtifactTableCard part={part} />;
      case "file":  return <ArtifactFileCard part={part} />;
      case "document": return <ArtifactDocCard part={part} />;
      // A missing or unrecognized kind (parse glitch, or a future kind this
      // client predates) must never render nothing — fall back to the code
      // card so the body is always shown rather than a blank card.
      default:      return <ArtifactCodeCard part={part} />;
    }
  })();
  // Export from the collapsed header, mirroring each card's own gate: a
  // complete markdown file, or a complete table whose body the route does not
  // 409 on. Anything else gets no button rather than one that always fails.
  // An export-only table is offered without reading its body: the server
  // sends none (ELASV1-3514), and the tool that made it wrote a table.
  // A tool may narrow the menu (export_formats); the route refuses the rest.
  const kindFormats =
    part.status !== "complete" ? null
    : part.artifact_kind === "file" && isMarkdownMime(part.mime) ? MARKDOWN_EXPORT_FORMATS
    : tableExportable ? TABLE_EXPORT_FORMATS
    : null;
  const exportFormats = kindFormats && exportFormatsFor(kindFormats, part.export_formats);
  const actions = exportFormats && (
    <ArtifactExportMenu artifactId={part.artifact_id} title={part.title} formats={exportFormats} />
  );
  // The approval bar stays outside the fold: a card waiting on approval must
  // never be collapsed out of sight.
  return (
    <>
      <CollapsibleArtifact
        artifactId={part.artifact_id}
        title={part.title}
        kind={part.artifact_kind}
        actions={actions}
        exportOnly={exportOnly}
        size={part.size}
        status={part.status}
        preview={exportOnly && part.preview ? <ArtifactTablePreview preview={part.preview} /> : undefined}
      >
        {inner}
      </CollapsibleArtifact>
      {part.status === "complete" && part.review_required && (
        <ArtifactApprovalBar artifactId={part.artifact_id} api={artifactsApi} />
      )}
    </>
  );
}
